import React, { useState, useMemo } from 'react';

import clsx from 'clsx';

import IconCancel from '@/assets/icons/icon-cancel.svg?react';
import IconSearch from '@/assets/icons/icon-search.svg?react';
import { Switch } from '@/components/inspector-fields/Switch/Switch';
import { TimeRangeSelector } from '@/components/analytics/TimeRangeSelector/TimeRangeSelector';
import { ABTestItem as ABTestItemComponent } from '@/components/analytics/ABTestItem/ABTestItem';
import type {
  ABTestItem,
  FilterItem,
  TimeRange,
  TrafficToggles,
  MetricToggles,
  ConversionToggles,
} from '@/components/analytics/types';
import type { DateRange } from '@/components/analytics/CustomRangeCalendar/CustomRangeCalendar';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectAnalyticsTimeRange,
  selectAnalyticsCustomRange,
  selectAnalyticsTraffic,
  selectAnalyticsMetrics,
  selectAnalyticsConversions,
} from '@/store/analytics/selectors';
import { selectIsFeatureEnabled } from '@/store/featureFlags/selectors';
import {
  setTimeRange,
  setCustomRange,
  setTrafficToggle,
  setMetricsToggle,
  setConversionsToggle,
} from '@/store/analytics/analyticsSlice';

interface AnalyticsSidebarProps {
  /** Active filter chips */
  filters?: FilterItem[];
  /** Called when a single filter chip is removed */
  onRemoveFilter?: (id: string) => void;
  /** Called when "Clear All" is clicked */
  onClearFilters?: () => void;
  /** Whether to show the A/B Test section with items */
  showAbTest?: boolean;
  /** A/B test items to display */
  abTests?: ABTestItem[];
  /** Called when an A/B test item is clicked */
  onAbTestSelect?: (id: string) => void;
  className?: string;
}

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="pb-1">
    <span className="text-xs font-semibold text-neutral-900">{children}</span>
  </div>
);

const Divider: React.FC = () => <div className="h-px w-full bg-neutral-100" />;

/* ──────────────────────────────────────────────────────────
   A/B Test section
   ────────────────────────────────────────────────────────── */
const ABTestSection: React.FC<{
  tests: ABTestItem[];
  onSelect?: (id: string) => void;
}> = ({ tests, onSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const filteredTests = useMemo(() => {
    if (!searchQuery) return tests;
    const q = searchQuery.toLowerCase();
    return tests.filter((t) => t.name.toLowerCase().includes(q));
  }, [tests, searchQuery]);

  const handleSelect = (id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(id);
      else next.delete(id);
      return next;
    });
    onSelect?.(id);
  };

  return (
    <div className="flex flex-col gap-4">
      <SectionHeader>A/B Test</SectionHeader>

      {/* Search */}
      <div className="flex h-9 items-center gap-2.5 rounded-md bg-neutral-50 px-2 py-1.5">
        <input
          type="text"
          aria-label="Search A/B tests"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search..."
          className="min-w-0 flex-1 bg-transparent text-xs leading-4 font-medium text-neutral-900 outline-none placeholder:text-neutral-500"
        />
        <IconSearch className="h-4 w-4 shrink-0 text-neutral-400" />
      </div>

      {/* Test items */}
      {filteredTests.length > 0 ? (
        <div className="flex flex-col gap-1">
          {filteredTests.map((test) => (
            <ABTestItemComponent
              key={test.id}
              id={test.id}
              name={test.name}
              status={test.status}
              selected={selectedIds.has(test.id)}
              onSelect={handleSelect}
            />
          ))}
        </div>
      ) : (
        <p className="text-[10px] leading-4 font-medium text-neutral-400">
          {tests.length === 0
            ? 'No A/B test created yet. Your A/B tests will be displayed here once created.'
            : 'No matching tests found.'}
        </p>
      )}
    </div>
  );
};

/* ──────────────────────────────────────────────────────────
   AnalyticsSidebar
   ────────────────────────────────────────────────────────── */
export const AnalyticsSidebar: React.FC<AnalyticsSidebarProps> = ({
  filters = [],
  onRemoveFilter,
  onClearFilters,
  showAbTest = false,
  abTests = [],
  onAbTestSelect,
  className,
}) => {
  const dispatch = useAppDispatch();
  const timeRange = useAppSelector(selectAnalyticsTimeRange);
  const serializedCustomRange = useAppSelector(selectAnalyticsCustomRange);
  const traffic = useAppSelector(selectAnalyticsTraffic);
  const metrics = useAppSelector(selectAnalyticsMetrics);
  const conversions = useAppSelector(selectAnalyticsConversions);
  const isAbTestEnabled = useAppSelector(selectIsFeatureEnabled('analytics-ab-test'));

  const customRange: DateRange | null = useMemo(
    () =>
      serializedCustomRange
        ? { start: new Date(serializedCustomRange.start), end: new Date(serializedCustomRange.end) }
        : null,
    [serializedCustomRange],
  );

  const handleTimeRangeChange = (value: TimeRange) => {
    dispatch(setTimeRange(value));
  };

  const handleCustomRangeChange = (range: DateRange) => {
    dispatch(setCustomRange(range));
  };

  const handleTrafficChange = (key: keyof TrafficToggles, value: boolean) => {
    dispatch(setTrafficToggle({ key, value }));
  };

  const handleMetricsChange = (key: keyof MetricToggles, value: boolean) => {
    dispatch(setMetricsToggle({ key, value }));
  };

  const handleConversionsChange = (key: keyof ConversionToggles, value: boolean) => {
    dispatch(setConversionsToggle({ key, value }));
  };

  return (
    <div
      className={clsx(
        'bg-neutral-0 inline-flex h-full w-72 flex-col items-center overflow-y-auto shadow-[1px_0_0_0_var(--color-neutral-100)] [scrollbar-width:none]',
        className,
      )}
    >
      <div className="flex w-full shrink-0 flex-col gap-4 p-6">
        {/* Time Range */}
        <div className="flex flex-col gap-4">
          <SectionHeader>Time Range</SectionHeader>
          <TimeRangeSelector
            value={timeRange}
            onChange={handleTimeRangeChange}
            customRange={customRange}
            onCustomRangeChange={handleCustomRangeChange}
            className="w-full"
          />
        </div>

        <Divider />

        {/* Traffic */}
        <div className="flex flex-col gap-4">
          <SectionHeader>Traffic</SectionHeader>
          <Switch
            label="Views"
            checked={traffic.views}
            onCheckedChange={(v) => handleTrafficChange('views', v)}
            className="gap-0!"
          />
          <Switch
            label="Unique Visitors"
            checked={traffic.uniqueVisitors}
            onCheckedChange={(v) => handleTrafficChange('uniqueVisitors', v)}
            className="gap-0!"
          />
          <Switch
            label="Conversions"
            checked={traffic.conversions}
            onCheckedChange={(v) => handleTrafficChange('conversions', v)}
            className="gap-0!"
          />
          <Switch
            label="Publish timestamps"
            checked={traffic.publishTimestamps}
            onCheckedChange={(v) => handleTrafficChange('publishTimestamps', v)}
            className="gap-0!"
          />
        </div>

        <Divider />

        {/* Metrics */}
        <div className="flex flex-col gap-4">
          <SectionHeader>Metrics</SectionHeader>
          <Switch
            label="Conversion Rate"
            checked={metrics.conversionRate}
            onCheckedChange={(v) => handleMetricsChange('conversionRate', v)}
            className="gap-0!"
          />
          <Switch
            label="Bounce Rate"
            checked={metrics.bounceRate}
            onCheckedChange={(v) => handleMetricsChange('bounceRate', v)}
            className="gap-0!"
          />
        </div>

        <Divider />

        {/* Conversions */}
        <div className="flex flex-col gap-4">
          <SectionHeader>Conversions</SectionHeader>
          <Switch
            label="Button Clicks"
            checked={conversions.buttonClicks}
            onCheckedChange={(v) => handleConversionsChange('buttonClicks', v)}
            className="gap-0!"
          />
          <Switch
            label="Form Submissions"
            checked={conversions.formSubmissions}
            onCheckedChange={(v) => handleConversionsChange('formSubmissions', v)}
            className="gap-0!"
          />
          <Switch
            label="Link Clicks"
            checked={conversions.linkClicks}
            onCheckedChange={(v) => handleConversionsChange('linkClicks', v)}
            className="gap-0!"
          />
        </div>

        <Divider />

        {/* Filters */}
        <div className="flex flex-col gap-2">
          <SectionHeader>Filters</SectionHeader>
          <p className="text-[10px] leading-4 font-medium text-neutral-400">
            Click any data point to add it as a filter.
          </p>
          {filters.length > 0 && (
            <div className="mt-1 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-neutral-900">Filters</span>
                {onClearFilters && (
                  <button
                    type="button"
                    onClick={onClearFilters}
                    className="cursor-pointer rounded-md px-2 py-1 text-[10px] leading-4 font-medium text-neutral-500 outline-1 -outline-offset-1 outline-neutral-200 transition-colors hover:bg-neutral-50"
                  >
                    Clear All
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {filters.map((filter) => (
                  <div
                    key={filter.id}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-neutral-100 py-1 pr-1.5 pl-2"
                  >
                    {filter.icon && (
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                        {filter.icon}
                      </span>
                    )}
                    <span className="text-xs leading-4 font-medium text-neutral-700">
                      {filter.label}
                    </span>
                    {onRemoveFilter && (
                      <button
                        type="button"
                        aria-label={`Remove ${filter.label} filter`}
                        onClick={() => onRemoveFilter(filter.id)}
                        className="flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-neutral-200"
                      >
                        <IconCancel className="h-3 w-3 text-neutral-400" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* A/B Testing – gated behind feature flag */}
        {isAbTestEnabled && (
          <>
            <Divider />
            {showAbTest ? (
              <ABTestSection tests={abTests} onSelect={onAbTestSelect} />
            ) : (
              <div className="flex flex-col gap-2">
                <SectionHeader>A/B Testing</SectionHeader>
                <p className="text-[10px] leading-4 font-medium text-neutral-400">
                  No A/B test created yet. Your A/B tests will be displayed here once created.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
